import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGeolocation } from '../hooks/useGeolocation';

interface PermissionModalProps {
    onClose?: () => void;
}

const PermissionModal: React.FC<PermissionModalProps> = ({ onClose }) => {
    const { notificationPermission, requestNotificationPermission, currentUser } = useAuth();
    const { checkPermissions: checkLocationPermission, requestPermissions: requestLocationPermission } = useGeolocation(currentUser?.uid);
    
    const [locationState, setLocationState] = useState<string>('loading');
    const [step, setStep] = useState<'intro' | 'location' | 'notifications' | 'denied'>('intro');
    const [isVisible, setIsVisible] = useState(false);
    const PERSISTENCE_KEY = 'yumii_notif_ignored';

    const checkAllPermissions = async () => {
        // Skip if user explicitly ignored it recently
        const isIgnored = localStorage.getItem(PERSISTENCE_KEY);
        if (isIgnored) return;

        let loc = 'default';
        if (currentUser) {
            try {
                loc = await checkLocationPermission();
                setLocationState(loc);
            } catch (e) {
                console.warn("Location permission check failed", e);
            }
        }

        const needsLocation = currentUser?.role === 'livreur' && loc !== 'granted';
        const needsNotifications = notificationPermission !== 'granted' && notificationPermission !== 'denied';

        if (needsLocation) {
            setStep('location');
            setIsVisible(true);
        } else if (needsNotifications) {
            setStep('notifications');
            setIsVisible(true);
        } else {
            setIsVisible(false);
            if (onClose) onClose();
        }
    };

    useEffect(() => {
        checkAllPermissions();
    }, [currentUser?.uid, notificationPermission]);

    const handleIgnore = () => {
        localStorage.setItem(PERSISTENCE_KEY, 'true');
        setIsVisible(false);
        if (onClose) onClose();
    };

    const handleLocationRequest = async () => {
        const success = await requestLocationPermission();
        if (success) {
            checkAllPermissions();
        } else {
            setStep('denied');
        }
    };

    const handleNotificationRequest = async () => {
        const newPerm = await requestNotificationPermission();
        if (newPerm === 'granted') {
            setIsVisible(false);
            if (onClose) onClose();
        } else {
            // Keep modal if denied locally but check again
            checkAllPermissions();
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
            <div className="bg-white w-full max-w-sm rounded-[2.5rem] shadow-2xl overflow-hidden animate-pop-in border border-gray-100 flex flex-col">
                
                {/* Header with Icon/Illustration */}
                <div className="pt-10 pb-6 flex justify-center">
                    <div className="relative">
                        <div className="absolute inset-0 bg-red-100 rounded-full scale-150 blur-xl opacity-50 animate-pulse"></div>
                        <div className="relative w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl shadow-xl flex items-center justify-center transform -rotate-6">
                            {step === 'location' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-8 pb-10 text-center">
                    <h2 className="text-2xl font-black text-gray-900 mb-2 leading-tight">
                        {step === 'location' ? "Où êtes-vous ?" : "Ne ratez rien !"}
                    </h2>
                    <p className="text-gray-500 font-medium text-sm leading-relaxed mb-8">
                        {step === 'location' 
                            ? "Activez la localisation pour découvrir les restaurants autour d'Alger et suivre vos livreurs en temps réel."
                            : "Activez les notifications pour savoir exactement quand votre commande est acceptée, préparée et arrive chez vous."}
                    </p>

                    <button
                        onClick={step === 'location' ? handleLocationRequest : handleNotificationRequest}
                        className="w-full py-4.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-2xl shadow-xl shadow-red-200 transition-all active:scale-[0.98] mb-4 text-lg"
                    >
                        {step === 'location' ? "Autoriser la position" : "Activer les notifications"}
                    </button>

                    <button
                        onClick={handleIgnore}
                        className="text-gray-400 text-sm font-bold hover:text-gray-600 transition-colors"
                    >
                        Peut-être plus tard
                    </button>
                </div>

                {/* Status Bar / Progress */}
                <div className="h-1.5 w-full bg-gray-100 flex">
                    <div className={`h-full bg-red-500 transition-all duration-500 ${step === 'location' ? 'w-1/2' : 'w-full'}`}></div>
                </div>
            </div>

            {/* Guide for Denied Permissions */}
            {step === 'denied' && (
                <div className="absolute inset-0 z-[10001] bg-black/90 flex flex-col items-center justify-center p-8 text-center text-white animate-fade-in">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
                         <span className="text-4xl">⚙️</span>
                    </div>
                    <h3 className="text-2xl font-bold mb-4">Action Requise</h3>
                    <p className="text-gray-300 mb-8 max-w-xs">
                        Vous avez bloqué la permission. Pour continuer, vous devez l'activer manuellement dans les paramètres de votre navigateur/téléphone.
                    </p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-8 py-3 bg-white text-black font-bold rounded-full shadow-lg"
                    >
                        Rafraîchir la page
                    </button>
                </div>
            )}
        </div>
    );
};

export default PermissionModal;
